import type {Annotation, ChangeSpec} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {buildNextMarkerSyntax, findItemPrefixes, isTasklistPrefix} from '../../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {isPositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {escapeMarkdownContinuation} from '../../extensions/markdown-continuation'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {getListSelection, remarkFollowingItems} from './utils'

/**
 * Dedent any selected list items, automatically adjusting numbering or list markers to match their destination.
 */
export const dedentSelectedListItems: Command = (view: EditorView) => {
  if (view.state.selection.ranges.length !== 1) return false

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()

  const selection = getListSelection(view.state)
  if (!selection) return false

  const parent = selection?.parents.at(-1)
  // The current parent will become the new previous sibling, so that's our starting point for the new prefixes
  let lastPrefix = parent && findItemPrefixes(parent.item).listItemPrefix?.raw

  const itemLinesByItem = selection.selectedItems.map(item =>
    MarkdownLine.linesAtRange(tree, source, item.position.start.offset, item.position.end.offset - 1),
  )

  const hasNonBlankContent = itemLinesByItem.some(lines => lines.some(line => !line.isBlank))

  // For each item, dedent each line of the item and also update the prefix to match the parent list level
  const itemsWithRemovedTaskListPrefix = new WeakSet<(typeof selection.selectedItems)[number]>()
  const changes: ChangeSpec[] = []

  for (const [itemIndex, item] of selection.selectedItems.entries()) {
    const itemLines = itemLinesByItem[itemIndex] ?? []
    for (const [index, line] of itemLines.entries()) {
      const prefixToUpdate = index === 0 ? line.prefixes.findLast(p => p.tokenType === 'listItemPrefix') : undefined
      const indentToRemove = line.prefixes.findLast(p => p.tokenType === 'listItemIndent')

      // Check if this is a task list item - if so, preserve the taskListPrefix
      const taskListPrefix = index === 0 ? line.prefixes.find(p => p.tokenType === 'taskListCheck') : undefined
      const contentIsTaskListMarker = index === 0 ? isTasklistPrefix(line.content) : false
      const hasIndentPrefix = line.prefixes.some(p => p.tokenType === 'listItemIndent')

      const shouldRemoveTaskListPrefix =
        index === 0 && line.isBlank && !hasIndentPrefix && (taskListPrefix !== undefined || contentIsTaskListMarker)

      if (shouldRemoveTaskListPrefix) {
        itemsWithRemovedTaskListPrefix.add(item)
      }

      // Determine the 'to' position: if there's a taskListPrefix, stop before it; otherwise use contentStart
      const toPosition =
        shouldRemoveTaskListPrefix && taskListPrefix && isPositionedNode(taskListPrefix)
          ? taskListPrefix.position.end.offset
          : shouldRemoveTaskListPrefix && contentIsTaskListMarker
            ? line.contentStart + line.content.length
            : (prefixToUpdate?.position?.end.offset ??
              indentToRemove?.position?.end.offset ??
              // If we have a taskListPrefix but no listItemPrefix, stop before the taskListPrefix
              taskListPrefix?.position?.start.offset ??
              line.contentStart)

      const change = {
        from: indentToRemove?.position?.start.offset ?? prefixToUpdate?.position?.start.offset ?? line.start,
        to: toPosition,
        insert: lastPrefix && prefixToUpdate ? (lastPrefix = buildNextMarkerSyntax(lastPrefix)) : '',
      }
      changes.push(change)
    }
  }

  // Any items that followed the dedented items at the same level now become sub-items and need to be renumbered
  const lastItemIndex = selection.list.children.indexOf(selection.selectedItems.at(-1)!)
  if (lastItemIndex > -1) changes.push(remarkFollowingItems(selection.list, lastItemIndex, null))

  const annotations: Array<Annotation<unknown>> = []

  // But wait, there's more!
  if (parent) {
    // The selected items got inserted into a parent list, so all items that will follow them need to be renumbered
    const parentItemIndex = parent.list.children.indexOf(parent.item)
    changes.push(remarkFollowingItems(parent.list, parentItemIndex, lastPrefix ?? ''))
  } else {
    // If there's no parent list (so this operation removes list formatting from the items), add surrounding blank
    // lines where necessary to disrupt 'lazy continuation' (https://github.github.com/gfm/#lazy-continuation-line)
    // If this is a tasklist, delete the taskListPrefix syntax since it's no longer a list item
    for (const item of selection.selectedItems) {
      const {taskListPrefix} = findItemPrefixes(item)
      if (taskListPrefix && isPositionedNode(taskListPrefix) && !itemsWithRemovedTaskListPrefix.has(item)) {
        changes.push({
          from: taskListPrefix.position.start.offset,
          to: taskListPrefix.position.end.offset,
        })
      }
    }

    if (hasNonBlankContent) {
      const prevLine = MarkdownLine.atOffset(tree, source, selection.selectedItems[0]!.position.start.offset - 1)
      const prevLineIsListItem = prevLine?.prefixes.some(
        p => p.tokenType === 'listItemPrefix' || p.tokenType === 'listItemIndent',
      )
      if (prevLineIsListItem) changes.push({from: prevLine!.end, insert: '\n'})

      const nextLine = MarkdownLine.atOffset(tree, source, selection.selectedItems.at(-1)!.position.end.offset + 1)
      const nextLineIsListItem = nextLine?.prefixes.some(
        p => p.tokenType === 'listItemPrefix' || p.tokenType === 'listItemIndent',
      )
      if (nextLineIsListItem) changes.push({from: nextLine!.start, insert: '\n'})

      annotations.push(escapeMarkdownContinuation.of('escapeList'))
    }
  }

  view.dispatch({
    scrollIntoView: true,
    userEvent: 'input',
    changes,
    annotations,
  })
  return true
}
